/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable @typescript-eslint/restrict-template-expressions */
import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, TableProps, Tooltip } from 'antd';
import { DownOutlined, PaperClipOutlined, WarningTwoTone } from '@ant-design/icons';
import { useModal } from '@ebay/nice-modal-react';
import './PackagingFlowTable.less';
import { useAuth } from '@isocel/front-common';
import moment from 'moment';

import {
    DocumentAttachment,
    GetAllPackagingFlowParams,
    LoadingSession,
    Package,
    PackagingFlowDirection,
    PackagingFlowItem,
    PackagingVoucher,
    StoreUser,
} from '../../../queries/api/types';
import { getAllPackagingFlow } from '../../../queries/api/packaging-flow';
import { fetchPackages } from '../../../queries/api/package';
import { useDashboardContext } from '../../../context/DashboardContext';
import TableDetailsFormDrawer from '../details/TableDetailsDrawer';
import { LOCATION_TYPE, PackagingFlowTypeMap } from '../../../constants';

interface DataType {
    key: string;
    sender: string;
    senderId: string;
    reciever: string;
    recieverId: string;
    date: string;
    unit: string;
    details: string;
    comment: string;
    packages?: PackagingFlowItem[];
    flowType?: PackagingFlowDirection;
    differenceReported: boolean;
    loadingSession?: string | LoadingSession | null;
    transportVoucher: string;
    transporter: string | null;
    received: boolean;
    originId: string | null;
    cmr: string;
    children?: DataType[];
    packagingVouchers?: PackagingVoucher[];
    userInfo?: string;
    attachments?: DocumentAttachment[];
    expeditionAt?: string;
    deliveredAt?: string;
    truckNumber?: string;
    orderNumber?: string;
    packagingVoucherUsed?: PackagingVoucher[];
}

const PackagingFlowTable = () => {
    const [data, setData] = useState<DataType[]>([]);
    const [packages, setPackages] = useState<any[]>([]);
    const [pagination, setPagination] = useState({ page: 0, pageSize: 10 });
    const [total, setTotal] = useState(0);
    const [sortField, setSortField] = useState<string | undefined>(undefined);
    const [sortOrder, setSortOrder] = useState<string | undefined>(undefined);
    const {
        selectedStoreIds,
        selectedPackageIds,
        selectedStatus,
        selectedVoucherStatus,
        differenceReported,
        selectedSupplierIds,
        selectedParcPalettIds,
        setSelectedStoreIds,
        selectedStartDate,
        selectedEndDate,
        selectedWarehouseIds,
        setHasTableData,
        storeFilterManuallySet,
    } = useDashboardContext();
    const { user } = useAuth();
    const storeId = (user as StoreUser)?.store || '';
    const tableDetailsFormDrawer = useModal(TableDetailsFormDrawer);

    useEffect(() => {
        fetchPackagesData();
        if (selectedStoreIds.length === 0 && storeId) {
            setSelectedStoreIds([storeId]);
        }
        setHasTableData(data.length > 0);
    }, [data.length, selectedStoreIds.length, setHasTableData, setSelectedStoreIds, storeId]);
    const getPackageModelName = useCallback(
        (pkg: string | Package) => {
            if (typeof pkg === 'string') {
                const packageObj = packages.find((p) => p.id === pkg);
                return packageObj?.model.name || '';
            }
            return pkg.model.name;
        },
        [packages]
    );

    const getPackageTypeName = useCallback(
        (pkg: string | Package) => {
            if (typeof pkg === 'string') {
                const packageObj = packages.find((p) => p.id === pkg);
                return packageObj?.type.name || '';
            }
            return pkg.type.name;
        },
        [packages]
    );

    const getPackageRental = useCallback(
        (pkg: string | Package) => {
            if (typeof pkg === 'string') {
                const packageObj = packages.find((p) => p.id === pkg);
                return packageObj?.rental || false;
            }
            return pkg.rental;
        },
        [packages]
    );
    const getPackageId = (pkg: string | Package) => {
        if (typeof pkg === 'string') {
            return pkg;
        }
        return pkg.id;
    };

    const fetchData = useCallback(
        async (params: GetAllPackagingFlowParams) => {
            try {
                const response = await getAllPackagingFlow(params, {
                    organization: { id: user?.organization?.id ?? '' },
                });
                if (!params.isMounted) return;
                const packagingFlows = response.items.map((flow) => {
                    const flowTypeMap = PackagingFlowTypeMap.get(flow.flowType);
                    const status = flowTypeMap?.label || 'Unknown';
                    const transferTypeMap: Record<string, string> = {
                        supplier: LOCATION_TYPE.TRANSPORTEUR,
                        palette: LOCATION_TYPE.PARC_PALETTE,
                        wareHouse: LOCATION_TYPE.ENTREPOT,
                        organization: LOCATION_TYPE.SCA,
                        provider: LOCATION_TYPE.PRESTATAIRE,
                        store: LOCATION_TYPE.MAGASIN,
                        // Add other potential transfer types
                    };
                    let recieverType = flowTypeMap?.recipientType || '';
                    let senderType = flowTypeMap?.senderType || '';

                    if (flow.flowType === PackagingFlowDirection.Transfer) {
                        // Use optional chaining here too in case recieverType or senderType are missing on flow
                        recieverType = transferTypeMap[flow.recieverType || ''] || recieverType;
                        senderType = transferTypeMap[flow.senderType || ''] || senderType;
                    } else if (
                        PackagingFlowDirection.Increase === flow.flowType ||
                        PackagingFlowDirection.Decrease === flow.flowType
                    ) {
                        recieverType = transferTypeMap[flow.recieverType] || recieverType;
                        senderType = `SCA`;
                    }

                    // populating package items
                    const allPackages = flow.packages.map((pkgItem: PackagingFlowItem) => {
                        return {
                            ...pkgItem,
                            model: getPackageModelName(pkgItem.package),
                            type: getPackageTypeName(pkgItem.package),
                            rental: getPackageRental(pkgItem.package),
                        };
                    });

                    let filteredPackages = [...allPackages];
                    if (selectedPackageIds.length > 0) {
                        filteredPackages = allPackages.filter((pkgItem) =>
                            selectedPackageIds.includes(getPackageId(pkgItem.package))
                        );
                    }

                    return {
                        key: flow._id,
                        sender: `${senderType || LOCATION_TYPE.UNKNOWN} - ${
                            flow.sender?.name || LOCATION_TYPE.UNKNOWN
                        }`,
                        senderId: flow.sender?._id,
                        recieverId: flow.recipient?._id,
                        reciever: `${recieverType || LOCATION_TYPE.UNKNOWN} - ${
                            flow.recipient?.name || LOCATION_TYPE.UNKNOWN
                        }`,
                        date: flow.expeditionAt
                            ? moment(flow.expeditionAt).format('DD/MM/YYYY  HH:mm')
                            : flow.deliveredAt
                            ? moment(flow.deliveredAt).format('DD/MM/YYYY  HH:mm')
                            : moment(flow.createdAt).format('DD/MM/YYYY  HH:mm'),
                        unit: filteredPackages
                            .reduce((acc, pkgItem) => acc + pkgItem.goodCount + pkgItem.badCount, 0)
                            .toString(),
                        packages: allPackages,
                        status: status,
                        differenceReported: flow.differenceReported,
                        details: '',
                        comment: flow.comment,
                        loadingSession: flow.loadingSession,
                        transportVoucher: flow.transportVoucher,
                        transporter: flow.transporter,
                        received: flow.received,
                        originId: flow.originId,
                        cmr: flow.cmr,
                        flowType: flow.flowType,
                        packagingVouchers: flow.packagingVouchers || [],
                        userInfo: flow.user,
                        attachments: flow.attachments,
                        expeditionAt:
                            flow.flowType === PackagingFlowDirection.ShippedToStore ||
                            flow.flowType === PackagingFlowDirection.ReceivedFromSupplierAtOrganization
                                ? !flow.expeditionAt
                                    ? moment(flow.createdAt).format('DD/MM/YYYY  HH:mm')
                                    : moment(flow.expeditionAt).format('DD/MM/YYYY  HH:mm')
                                : moment(flow.createdAt).format('DD/MM/YYYY  HH:mm'),
                        deliveredAt: moment(flow.deliveredAt).format('DD/MM/YYYY  HH:mm'),
                        truckNumber: flow.truckNumber,
                        orderNumber: flow.orderNumber,
                        packagingVoucherUsed: flow.packagingVoucherUsed || [],
                        children: filteredPackages.map((pkgItem) => ({
                            sender: '',
                            reciever: '',
                            date: '',
                            unit: '',
                            packages: [
                                {
                                    ...pkgItem,
                                },
                            ],
                            loadingSession: [],
                            packagingVouchers: [],
                            transportVoucher: '',
                            transporter: '',
                            cmr: '',
                            originId: '',
                            received: false,
                            flowType: null,
                            differenceReported: false,
                            userInfo: null,
                            attachments: [],
                            expeditionAt: '',
                            deliveredAt: '',
                            truckNumber: '',
                            orderNumber: '',
                        })),
                    };
                });
                if (params.isMounted) {
                    setData(packagingFlows as any);
                    setTotal(response.totalCount);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        },
        [user?.organization?.id, selectedPackageIds, getPackageModelName, getPackageTypeName, getPackageRental]
    );
    const fetchPackagesCount = useCallback(async () => {
        if (!storeFilterManuallySet && selectedStoreIds.length === 0 && storeId) {
            setSelectedStoreIds([storeId]);
        }
        // ...rest of your logic
    }, [storeId, setSelectedStoreIds, selectedStoreIds, storeFilterManuallySet]);

    useEffect(() => {
        let isMounted = true;
        if (packages.length) {
            fetchData({
                page: pagination.page,
                pageSize: pagination.pageSize,
                differenceReported,
                packageIds: selectedPackageIds,
                status: selectedStatus,
                voucherStatus: selectedVoucherStatus,
                storeIds: selectedStoreIds,
                supplierIds: selectedSupplierIds,
                parcPalettIds: selectedParcPalettIds,
                warehouseIds: selectedWarehouseIds,
                sortField,
                sortOrder,
                isMounted,
                startDate: selectedStartDate,
                endDate: selectedEndDate,
            });
        }

        return () => {
            isMounted = false;
        };
    }, [
        pagination,
        packages,
        selectedPackageIds,
        selectedStatus,
        selectedStoreIds,
        sortField,
        sortOrder,
        differenceReported,
        selectedSupplierIds,
        selectedParcPalettIds,
        selectedWarehouseIds,
        selectedStartDate,
        selectedEndDate,
        fetchData,
        selectedVoucherStatus,
    ]);

    const fetchPackagesData = async () => {
        const response = await fetchPackages();
        setPackages(response);
    };

    const handleTableChange: TableProps<DataType>['onChange'] = (pagination, filter, sorter) => {
        setPagination({ page: (pagination.current ?? 1) - 1, pageSize: pagination.pageSize ?? 10 });

        if (Array.isArray(sorter)) {
            if (sorter.length > 0) {
                const sortField = sorter[0].field === 'unit' ? 'totalQuantity' : sorter[0].field;
                setSortField(sortField as string);
                setSortOrder(sorter[0].order === 'ascend' ? 'asc' : 'desc');
            }
        } else {
            const sortField = sorter.field === 'unit' ? 'totalQuantity' : sorter.field;
            setSortField(sortField as string);
            setSortOrder(sorter.order === 'ascend' ? 'asc' : 'desc');
        }
    };

    const handleDetailsClick = (record: DataType) => {
        tableDetailsFormDrawer.show({ data: record, storeId, fetchPackagesCount });
    };

    const columns = [
        {
            title: 'Émetteur',
            dataIndex: 'sender',
            key: 'sender',
        },
        {
            title: 'Destinataire',
            dataIndex: 'reciever',
            key: 'reciever',
        },
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            sorter: true,
        },
        {
            title: 'Unité',
            dataIndex: 'unit',
            key: 'unit',
            sorter: true,
            render: (_: string, record: DataType) => (
                <>
                    {record.children && <div>{record.unit}</div>}
                    {!record.children &&
                        record.packages?.map((pkg, index) => (
                            <div key={`${index}`}>
                                {pkg.goodCount + pkg.badCount} -{' '}
                                {getPackageTypeName(pkg.package) === getPackageModelName(pkg.package)
                                    ? getPackageTypeName(pkg.package)
                                    : `${getPackageTypeName(pkg.package)} ${getPackageModelName(pkg.package)}`}
                            </div>
                        ))}
                </>
            ),
        },
        {
            title: 'Statut',
            dataIndex: 'flowType',
            key: 'flowType',
            render: (flowType: number) => {
                let className = 'tag ';
                const flowTypeMap = PackagingFlowTypeMap.get(flowType);
                className += flowTypeMap?.tagClass || '';
                return <div className={className}>{flowTypeMap?.label || ''}</div>;
            },
        },
        {
            title: 'Bon de transport',
            dataIndex: 'transportVoucher',
            key: 'transportVoucher',
            render: (_: string, record: DataType) => {
                if (!record.children) {
                    return null;
                }
                return <div>{record.transportVoucher}</div>;
            },
        },
        {
            title: 'Action',
            key: 'action',
            render: (_: any, record: DataType) => {
                if (!record.children) {
                    return null;
                }
                return (
                    <Button size="small" type="primary" onClick={() => handleDetailsClick(record)}>
                        Détails
                    </Button>
                );
            },
        },
        {
            title: 'Alerte écart',
            dataIndex: 'differenceReported',
            key: 'differenceReported',
            render: (_: any, record: DataType) => {
                const icons = [];
                if (record.differenceReported) {
                    icons.push(
                        <Tooltip
                            key="differenceReported"
                            title="La réception déclarée est différente de l'expédition. Veuillez cliquer sur 'Détails' pour en savoir plus"
                        >
                            <WarningTwoTone twoToneColor="#f08e02" className="px-16" />
                        </Tooltip>
                    );
                    if (record.attachments && record.attachments.length > 0) {
                        icons.push(<PaperClipOutlined key="attachments" className="text-blue" />);
                    }
                }
                return <div>{icons}</div>;
            },
        },
    ];

    return (
        <div>
            <div className="table-container">
                <Table
                    aria-label="Flux récents"
                    size="small"
                    columns={columns}
                    dataSource={data}
                    pagination={{
                        current: pagination.page + 1,
                        pageSize: pagination.pageSize,
                        total: total,
                        position: ['bottomRight'],
                        onChange: (p, ps) => {
                            setPagination({ page: p - 1, pageSize: ps });
                        },
                    }}
                    onChange={handleTableChange}
                    expandable={{
                        expandIcon: ({ expanded, onExpand, record }) => {
                            if (!record.children) return '';
                            return (
                                <DownOutlined
                                    className="expand-icon"
                                    onClick={(e) => {
                                        onExpand(record, e);
                                    }}
                                    style={{
                                        transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                    }}
                                />
                            );
                        },
                    }}
                    title={() => (
                        <table>
                            <caption className="w-full flex">Flux récents</caption>
                        </table>
                    )}
                ></Table>
            </div>
        </div>
    );
};

export default PackagingFlowTable;
